import { useState, useCallback } from "react";
import { Flex, Button, AlertDialog, Tooltip, Callout } from "@radix-ui/themes";
import {
  TrashIcon,
  UploadIcon,
  ExclamationTriangleIcon,
  CheckCircledIcon,
} from "@radix-ui/react-icons";
import { useTranslation } from "react-i18next";
import { Manager } from "../../../Manager";
import { useManagerContext } from "../../../Manager/context";
import type { SearchParams, SortParams } from "../../../Manager/types";
import {
  fetchImportActivities,
  deleteImportActivities,
  fetchAllImportActivityIds,
  fetchDistinctValuesForImport,
  type ImportActivity,
} from "../../../../state/import-activities";
import type { ImportMeta } from "../../../../lib/import-db";
import { commitImportActivities } from "../../../../lib/commit-import";

interface FilterTabProps {
  importRecord: ImportMeta;
  onActivitiesRejected?: () => void;
  onActivitiesCommitted?: () => void;
}

// Inner component that has access to Manager context
function FilterTabActions({
  onActivitiesRejected,
  onActivitiesCommitted,
  t,
}: {
  onActivitiesRejected?: () => void;
  onActivitiesCommitted?: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const { selectedIds, selectionCount, clearSelection, refresh } =
    useManagerContext<ImportActivity>();
  const [rejecting, setRejecting] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showCommitConfirm, setShowCommitConfirm] = useState(false);

  const handleReject = async () => {
    if (selectedIds.size === 0) return;

    setRejecting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await deleteImportActivities(Array.from(selectedIds) as number[]);
      clearSelection();
      refresh();
      onActivitiesRejected?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRejecting(false);
    }
  };

  const handleCommit = async () => {
    if (selectedIds.size === 0) return;

    setCommitting(true);
    setError(null);
    setSuccessMessage(null);
    setShowCommitConfirm(false);

    try {
      const result = await commitImportActivities(
        Array.from(selectedIds) as number[],
      );

      if (result.success) {
        setSuccessMessage(
          t("import.commitSuccess", {
            count: result.committedCount,
            activities:
              result.committedCount === 1
                ? t("import.activity")
                : t("import.activities"),
          }),
        );
        clearSelection();
        refresh();
        onActivitiesCommitted?.();
      } else {
        setError(result.errors.join("\n"));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCommitting(false);
    }
  };

  return (
    <>
      <Flex gap="2">
        <Button
          variant="solid"
          color="green"
          size="2"
          disabled={selectionCount === 0 || committing || rejecting}
          onClick={() => setShowCommitConfirm(true)}
        >
          <UploadIcon />
          {committing ? t("import.committing") : t("import.commit")}
        </Button>

        <AlertDialog.Root
          open={showCommitConfirm}
          onOpenChange={setShowCommitConfirm}
        >
          <AlertDialog.Content>
            <AlertDialog.Title>{t("import.commitTitle")}</AlertDialog.Title>
            <AlertDialog.Description>
              {t("import.commitDescription", {
                count: selectionCount,
                activities:
                  selectionCount === 1
                    ? t("import.activity")
                    : t("import.activities"),
              })}
            </AlertDialog.Description>
            <Flex gap="3" mt="4" justify="end">
              <AlertDialog.Cancel>
                <Button variant="soft" color="gray">
                  {t("common.cancel")}
                </Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button variant="solid" color="green" onClick={handleCommit}>
                  {t("import.commitSelected")}
                </Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>

        <AlertDialog.Root>
          <AlertDialog.Trigger>
            <Button
              variant="soft"
              color="red"
              size="2"
              disabled={selectionCount === 0 || rejecting || committing}
            >
              <TrashIcon />
              {rejecting ? t("import.rejecting") : t("import.reject")}
            </Button>
          </AlertDialog.Trigger>
          <AlertDialog.Content>
            <AlertDialog.Title>{t("import.rejectTitle")}</AlertDialog.Title>
            <AlertDialog.Description>
              {t("import.rejectDescription", {
                count: selectionCount,
                activities:
                  selectionCount === 1
                    ? t("import.activity")
                    : t("import.activities"),
              })}
            </AlertDialog.Description>
            <Flex gap="3" mt="4" justify="end">
              <AlertDialog.Cancel>
                <Button variant="soft" color="gray">
                  {t("common.cancel")}
                </Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button variant="solid" color="red" onClick={handleReject}>
                  {t("import.rejectSelected")}
                </Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>
      </Flex>

      {successMessage && (
        <Callout.Root color="green" size="1" mt="2">
          <Callout.Icon>
            <CheckCircledIcon />
          </Callout.Icon>
          <Callout.Text>{successMessage}</Callout.Text>
        </Callout.Root>
      )}

      {error && (
        <Callout.Root color="red" size="1" mt="2">
          <Callout.Icon>
            <ExclamationTriangleIcon />
          </Callout.Icon>
          <Callout.Text style={{ whiteSpace: "pre-line" }}>
            {error}
          </Callout.Text>
        </Callout.Root>
      )}
    </>
  );
}

export function FilterTab({
  importRecord,
  onActivitiesRejected,
  onActivitiesCommitted,
}: FilterTabProps) {
  const { t } = useTranslation();
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchData = useCallback(
    async (
      limit?: number,
      offset?: number,
      searchParams?: SearchParams,
      sortParams?: SortParams,
    ) => {
      // importRecord.id is passed for API compatibility but not actually used
      return fetchImportActivities(
        importRecord.id ?? 0,
        limit,
        offset,
        searchParams,
        sortParams,
      );
    },
    [importRecord.id],
  );

  const fetchAllIds = useCallback(
    async (searchParams?: SearchParams) => {
      // importRecord.id is passed for API compatibility but not actually used
      return fetchAllImportActivityIds(importRecord.id ?? 0, searchParams);
    },
    [importRecord.id],
  );

  // Fetch distinct values for DynamicSearch filters
  const fetchDistinctValues = useCallback(
    async (
      filterKey: string,
      otherFilters?: Record<string, unknown>,
    ): Promise<{ value: string | number; label: string }[]> => {
      return fetchDistinctValuesForImport(filterKey, otherFilters);
    },
    [],
  );

  const handleActivitiesRejected = useCallback(() => {
    setRefreshKey((k) => k + 1);
    onActivitiesRejected?.();
  }, [onActivitiesRejected]);

  const handleActivitiesCommitted = useCallback(() => {
    setRefreshKey((k) => k + 1);
    onActivitiesCommitted?.();
  }, [onActivitiesCommitted]);

  return (
    <Manager.Root<ImportActivity>
      key={refreshKey}
      title=""
      fetchData={fetchData}
      limit={50}
      allowSelection
      getItemId={(a) => a.id}
      fetchAllIds={fetchAllIds}
      actionButtons={
        <FilterTabActions
          onActivitiesRejected={handleActivitiesRejected}
          onActivitiesCommitted={handleActivitiesCommitted}
          t={t}
        />
      }
    >
      <Manager.Search>
        <Manager.DynamicSearch.Root fetchDistinctValues={fetchDistinctValues}>
          <Manager.DynamicSearch.Filter
            filterKey="periods"
            label={t("import.periods")}
            defaultExpanded
          />
          <Manager.DynamicSearch.Filter
            filterKey="suppliers"
            label={t("import.suppliers")}
          />
          <Manager.DynamicSearch.Filter
            filterKey="products"
            label={t("import.products")}
          />
        </Manager.DynamicSearch.Root>
        <Manager.BinarySearch
          filterKey="hasProblem"
          label={t("import.hasProblem")}
          yesLabel={t("import.yes")}
          noLabel={t("import.no")}
          allLabel={t("import.all")}
        />
      </Manager.Search>

      <Manager.Sort
        columns={[
          { key: "type_name" },
          { key: "store_count" },
          { key: "budget" },
        ]}
      />

      <Manager.Table>
        <Manager.Column<ImportActivity>
          header={t("import.info")}
          width="60px"
          accessor={(a) =>
            a.problem ? (
              <Tooltip content={a.problem}>
                <ExclamationTriangleIcon
                  color="var(--amber-9)"
                  style={{ cursor: "help" }}
                />
              </Tooltip>
            ) : null
          }
        />
        <Manager.Column<ImportActivity>
          header={t("import.activityType")}
          accessor={(a) => a.type_name}
          sortKey="type_name"
        />
        <Manager.Column<ImportActivity>
          header={t("import.period")}
          accessor={(a) => a.period_reference}
        />
        <Manager.Column<ImportActivity>
          header={t("import.productCode")}
          accessor={(a) => a.product_reference}
        />
        <Manager.Column<ImportActivity>
          header={t("import.productName")}
          accessor={(a) => a.product_name ?? "-"}
        />
        <Manager.Column<ImportActivity>
          header={t("import.supplier")}
          accessor={(a) => a.supplier_name ?? "-"}
        />
        <Manager.Column<ImportActivity>
          header={t("import.budget")}
          width="120px"
          sortKey="budget"
          accessor={(a) =>
            a.budget != null
              ? a.budget.toLocaleString("en-CA", {
                  style: "currency",
                  currency: "CAD",
                })
              : "-"
          }
        />
        <Manager.Column<ImportActivity>
          header={t("import.stores")}
          sortKey="store_count"
          width="100px"
          accessor={(a) => a.store_count}
        />
      </Manager.Table>
    </Manager.Root>
  );
}
